import { cn } from '@/lib/cn'
import { Link, Outlet, createRoute, useLocation } from '@tanstack/react-router'
import { FileText, Globe, LayoutDashboard, type LucideIcon } from 'lucide-react'
import { Route as appLayoutRoute } from '../layout'

interface CmsNavItem {
  to: string
  label: string
  icon: LucideIcon
  group: 'overview' | 'content'
}

const ITEMS: CmsNavItem[] = [
  { to: '/cms', label: 'Dashboard', group: 'overview', icon: LayoutDashboard },
  { to: '/cms/articles', label: 'Articles', group: 'content', icon: FileText },
  { to: '/cms/content', label: 'Contenu site', group: 'content', icon: Globe },
]

function GroupLabel({ children }: { children: string }) {
  return (
    <div className="mt-4 px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground first:mt-0">
      {children}
    </div>
  )
}

function NavLink({ item, active }: { item: CmsNavItem; active: boolean }) {
  const Icon = item.icon
  return (
    <Link
      to={item.to}
      className={cn(
        'group flex h-10 items-center gap-2.5 rounded-xl px-3 text-sm transition-all',
        active
          ? 'bg-primary font-medium text-primary-foreground shadow-sm'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      <Icon
        className={cn(
          'h-4 w-4 shrink-0 transition-transform',
          active ? '' : 'group-hover:scale-110',
        )}
        aria-hidden="true"
      />
      <span className="truncate">{item.label}</span>
    </Link>
  )
}

function CmsSideNav() {
  const location = useLocation()
  const overview = ITEMS.filter((i) => i.group === 'overview')
  const content = ITEMS.filter((i) => i.group === 'content')

  function isActive(item: CmsNavItem) {
    if (item.to === '/cms') return location.pathname === '/cms'
    return location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)
  }

  return (
    <nav className="flex w-[220px] shrink-0 flex-col gap-1">
      <GroupLabel>Vue d'ensemble</GroupLabel>
      {overview.map((item) => (
        <NavLink key={item.to} item={item} active={isActive(item)} />
      ))}
      <GroupLabel>Gestion contenu</GroupLabel>
      {content.map((item) => (
        <NavLink key={item.to} item={item} active={isActive(item)} />
      ))}
    </nav>
  )
}

function CmsMobileTabs() {
  const location = useLocation()

  function isActive(item: CmsNavItem) {
    if (item.to === '/cms') return location.pathname === '/cms'
    return location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)
  }

  return (
    <div className="-mx-4 overflow-x-auto pb-1 md:hidden">
      <div className="flex gap-2 px-4">
        {ITEMS.map((item) => {
          const Icon = item.icon
          const active = isActive(item)
          return (
            <Link
              key={item.to}
              to={item.to}
              className={cn(
                'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-all',
                active
                  ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                  : 'border-border bg-background text-foreground hover:bg-muted',
              )}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              {item.label}
            </Link>
          )
        })}
      </div>
    </div>
  )
}

function CmsLayout() {
  return (
    <div className="flex flex-col gap-6">
      <CmsMobileTabs />
      <div className="grid grid-cols-1 gap-8 md:grid-cols-[220px_1fr]">
        <aside className="hidden md:block">
          <CmsSideNav />
        </aside>
        <div className="min-w-0">
          <Outlet />
        </div>
      </div>
    </div>
  )
}

export const Route = createRoute({
  getParentRoute: () => appLayoutRoute,
  id: 'cms-layout',
  component: CmsLayout,
})
